import { SmartActionsInputProps } from '@bitte-ai/types';
import { ArrowUpRight } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

interface WidgetChatInputProps extends SmartActionsInputProps {
  autoFocus?: boolean;
}

export const WidgetChatInput = ({
  input,
  isLoading,
  handleChange,
  handleSubmit,
  borderColor,
  textColor,
  backgroundColor,
  placeholderText,
  autoFocus = false,
}: WidgetChatInputProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!autoFocus || !textareaRef.current || !containerRef.current) return;

    // Create intersection observer to detect when widget becomes visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            setTimeout(() => {
              if (textareaRef.current) {
                textareaRef.current.focus();
                textareaRef.current.scrollIntoView({ block: 'nearest' });
              }
            }, 100);
          }
        });
      },
      {
        threshold: 0.5,
        rootMargin: '10px',
      },
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [autoFocus]);

  // Only sync when input is cleared externally (like after form submission)
  useEffect(() => {
    if (textareaRef.current && input === '') {
      textareaRef.current.value = '';
    }
  }, [input]);

  // Fallback: Focus on any click in the widget area
  const handleContainerClick = () => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  return (
    <div ref={containerRef} onClick={handleContainerClick}>
      <form
        className="bitte-relative bitte-mb-0 bitte-flex bitte-flex-col bitte-w-full bitte-items-center bitte-justify-center bitte-gap-2 bitte-bg-mb-gray-150"
        style={{ color: textColor }}
        onSubmit={handleSubmit}
      >
        <div className="bitte-w-full">
          <Textarea
            ref={textareaRef}
            placeholder={placeholderText || 'Type your message...'}
            style={{
              background: backgroundColor,
              borderColor: borderColor,
            }}
            className="bitte-h-[42px] bitte-w-full bitte-resize-none bitte-min-h-0 textarea-chat"
            onChange={handleChange}
            onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
              if (e.key === 'Enter' && !e.shiftKey && !isLoading) {
                e.preventDefault();
                handleSubmit(e as unknown as React.FormEvent<HTMLFormElement>);
              }
            }}
          />
        </div>

        <Button
          type="submit"
          disabled={!input || isLoading}
          className="bitte-h-[42px] bitte-w-full bitte-p-0 disabled:bitte-opacity-20 bitte-text-black bitte-bg-white"
        >
          <span>Send</span>
        </Button>
        <a
          href="https://bitte.ai"
          target="_blank"
          rel="noopener noreferrer"
          className="bitte-text-[#6E6E6E] hover:bitte-text-white bitte-flex bitte-items-center bitte-gap-1"
        >
          <span className="bitte-text-[10px] bitte-font-semibold">
            Powered by Bitte
          </span>
          <ArrowUpRight className="bitte-w-2 bitte-h-2" />
        </a>
      </form>
    </div>
  );
};
